import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api } from '../services/api';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { User, Mail, Lock, ArrowLeft } from 'lucide-react';

export default function RegisterPage() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [registrationEnabled, setRegistrationEnabled] = useState(true);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    api<{ registrationEnabled: boolean }>('/config')
      .then((d) => setRegistrationEnabled(d.registrationEnabled ?? true))
      .catch(() => setRegistrationEnabled(true))
      .finally(() => setChecking(false));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await api('/auth/register', {
        method: 'POST',
        body: JSON.stringify({ name, email, password }),
      });
      navigate('/login');
    } catch (err: any) {
      setError(err.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-[var(--color-background)] p-4">
        <div className="mb-8 flex items-center justify-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--color-primary)] text-2xl font-bold text-white shadow-lg">J</div>
        </div>
        <div className="text-sm text-[var(--color-muted-foreground)]">Loading…</div>
      </div>
    );
  }

  if (!registrationEnabled) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-[var(--color-background)] p-4 animate-in">
        <div className="mb-8 flex items-center justify-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--color-primary)] text-2xl font-bold text-white shadow-lg">J</div>
        </div>
        <Card className="w-full max-w-sm shadow-lg border-0 ring-1 ring-[var(--color-border)] text-center">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-center text-2xl font-bold tracking-tight">Jani</CardTitle>
            <CardDescription className="text-center">Registration is currently disabled</CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/login" className="inline-flex items-center text-sm text-[var(--color-primary)] hover:underline"><ArrowLeft className="mr-1 h-4 w-4" /> Back to login</Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[var(--color-background)] p-4 animate-in">
      <div className="mb-8 flex items-center justify-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--color-primary)] text-2xl font-bold text-white shadow-lg">J</div>
      </div>
      <Card className="w-full max-w-sm shadow-lg border-0 ring-1 ring-[var(--color-border)]">
        <CardHeader className="space-y-1 pb-4">
          <CardTitle className="text-center text-2xl font-bold tracking-tight">Jani</CardTitle>
          <CardDescription className="text-center">Create your account</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <Badge variant="destructive" className="w-full justify-center py-1.5 text-xs font-medium rounded-md">
                {error}
              </Badge>
            )}
            <div className="space-y-2">
              <Label htmlFor="name" className="text-xs font-semibold uppercase tracking-wider text-[var(--color-muted-foreground)]">Name</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-muted-foreground)]" />
                <Input id="name" type="text" value={name} onChange={e => setName(e.target.value)} className="pl-10" placeholder="Your name" required />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-[var(--color-muted-foreground)]">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-muted-foreground)]" />
                <Input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} className="pl-10" placeholder="name@company.com" required />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-[var(--color-muted-foreground)]">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-muted-foreground)]" />
                <Input id="password" type="password" value={password} onChange={e => setPassword(e.target.value)} className="pl-10" placeholder="••••••••" required minLength={8} />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-xs font-semibold uppercase tracking-wider text-[var(--color-muted-foreground)]">Confirm Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-muted-foreground)]" />
                <Input id="confirmPassword" type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="pl-10" placeholder="••••••••" required minLength={8} />
              </div>
              {password && confirmPassword && password !== confirmPassword && (
                <p className="text-xs text-[var(--color-destructive)]">Passwords do not match</p>
              )}
            </div>
            <Button data-testid="register-button" type="submit" disabled={loading || password !== confirmPassword} loading={loading} className="w-full">Create Account</Button>
          </form>
          <div className="mt-4 text-center text-sm">
            <Link to="/login" className="text-[var(--color-primary)] hover:underline">Already have an account? Log in</Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
